import React, { useEffect, useState } from "react";
import { Card, Button } from "react-bootstrap";
import { IActionHandler, MessageDisplayType, WebsocketCryptoMessage } from "@preveil-api";
import { useAppDispatch, useAppSelector, SettingsErrorMessages, MessageHandlerDisplayType, Message, AccountWSSteps, GlobalErrorMessages, MessageAnchors, ConfigKeys } from "src/common";
import { Loading, PageHeader } from "src/components";
import { DefaultPanel, InitConnection, ConnectionEstablished, SuccessPanel, ErrorPanel, AwaitConnection } from ".";
import { uiActions, WebsocketStatus, websocketActions, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";

// Constants.ALERT_ERROR, "Cannot approve recovery for self - use Add Device instead", "", 5));
function ApproveRecoveryComponent() {
	const dispatch = useAppDispatch();
	// selectors:
	const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
	const status = useAppSelector((state: RootState) => state.websocket.status);
	const errors = useAppSelector((state: RootState) => state.websocket.errors);
	const user_id = useAppSelector((state: RootState) => state.account.current_account?.user_id);
	const [step, setStep] = useState<number>(AccountWSSteps.DISCONNECTED);
	const [request_user_id, setRequestUserID] = useState<string>();
	const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);

	// Description: First onload and unload events - destroy ws on leave
	useEffect(() => {
		return () => {  // Destroy websocket State on leave
			dispatch(websocketActions.destroyWebsocket());
		};
	}, []);

	// Description: Responds to changes in message and websocket status
	useEffect(() => {
		const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
		let next = is_error ? AccountWSSteps.ERROR : !!message ? step + 1 : step;
		if (!!message?.status && message.status === WebsocketStatus.key_transfer_complete) {
			next = AccountWSSteps.GET_KEY_TRANSFER_COMPLETE;
		}
		setStep(next);
	}, [message, status]);

	useEffect(() => {
		!!errors &&
			ApproveRecoveryRequests.handlePageErrorMessage({ message: SettingsErrorMessages.default, stack: errors });
	}, [errors]);

	const ApproveRecoveryRequests = {
		// Description: Acknowledge information and begin ws process
		handleSubmitAcknowledgeInformation: () => {
			// setAcknowledged(true);
			setStep(AccountWSSteps.INITIALIZE_CONNECTION);
		},
		// Description: Initialize the connection
		handleInitWebsocketConnection: (_request_user_id: string) => {
			if (!!user_id && !!_request_user_id) {
				const host = `${crypto_websocket_server}/get/account/${encodeURIComponent(user_id)}/shard/${encodeURIComponent(_request_user_id)}/send`;
				setRequestUserID(_request_user_id);
				dispatch(websocketActions.wsConnect({ host, init: { action: "init" } }));
			} else {
				ApproveRecoveryRequests.handlePageError({ message: SettingsErrorMessages.missing_user_id });
			}
		},
		// Description: Send Pin to server to request adding a device
		handleTransferKeyRequest: (params: { pin: string }) => {
			if (!!params.pin && params.pin.length > 0) {
				const verify_message: string = JSON.stringify({
					user_id,
					action: "verify",
					pin: params.pin
				});
				dispatch(websocketActions.wsOnSendMessage(verify_message));
			} else {
				ApproveRecoveryRequests.handlePageError({ message: SettingsErrorMessages.bad_pin });
			}
		},
		// Description: Reset message state and ws connection
		handleResetMessage: () => {
			dispatch(websocketActions.wsOnResetMessage());
		},
		// Description: Reset   ws connection
		handleDisconnect: () => {
			dispatch(websocketActions.wsDisconnect());
		},
		// Description: Reset page state and ws connection
		handleDestroy: () => {
			dispatch(websocketActions.wsDisconnect());
			dispatch(websocketActions.destroyWebsocket());
			setStep(AccountWSSteps.DISCONNECTED);
		},
		handlePageError: (params: { message: string, stack?: any, displayType?: MessageDisplayType }) => {
			const display_type = params.displayType || MessageHandlerDisplayType.toastr;
			dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
		},
		handlePageErrorMessage: (params: { message: string, stack?: any, displayType?: MessageDisplayType }) => {
			const display_type = params.displayType || MessageHandlerDisplayType.logger;
			dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
		}
	};

	//  Description: Handle all actions from Children forms
	function handlePageActions(action_obj: IActionHandler) {
		const callback = `handle${action_obj.actionType}`;
		if ((ApproveRecoveryRequests as any)[callback] instanceof Function) {
			(ApproveRecoveryRequests as any)[callback](action_obj.params);
		} else {
			const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
			ApproveRecoveryRequests.handlePageErrorMessage({ message, stack: action_obj });
		}
	};

	// Description: Allow user to reset form if ws gets stuck or connection is disrupted
	function InPlaceLoading() {
		return <>
			<Loading className="in-place" />
			<Button onClick={() => ApproveRecoveryRequests.handleDestroy()} >Try again</Button>
		</>;
	}

	const ApproveRecoveryPanels = () => {
		switch (step) {
			case AccountWSSteps.ERROR:
				return <ErrorPanel error={SettingsErrorMessages.default} handleAction={handlePageActions} />;
			case AccountWSSteps.DISCONNECTED:
				return <DefaultPanel handleAction={handlePageActions} />;
			case AccountWSSteps.INITIALIZE_CONNECTION:
				return <InitConnection handleAction={handlePageActions} />;
			case AccountWSSteps.GET_ACCEPTED_INIT_OK:
				return !!request_user_id ?
					<AwaitConnection handleAction={handlePageActions} request_user_id={request_user_id} /> : <InPlaceLoading />;
			case AccountWSSteps.GET_CONNECTION_ESTABLISHED:
				return !!request_user_id ?
					<ConnectionEstablished handleAction={handlePageActions} request_user_id={request_user_id} /> : <InPlaceLoading />;
			case AccountWSSteps.GET_KEY_TRANSFER_COMPLETE:
				return <SuccessPanel request_user_id={request_user_id} handleAction={handlePageActions} />;
			default:
				return <InPlaceLoading />;
		};
	};

	return <>
		<PageHeader>
			<h1>Recovery Approval</h1>
		</PageHeader>
		<Card className="pv-card">
			{ApproveRecoveryPanels()}
		</Card>
	</>;
}

export default React.memo(ApproveRecoveryComponent);
